
/** @type {NodeList} */
const successStateUI = petition.querySelectorAll('.reveal:not(#petition-error)');
const buttonBar = document.getElementById('petition-mobile-cta');

window.addEventListener('DOMContentLoaded', () => {
	document.forms.sign.addEventListener('submit', signPetition);
	maybeFadeOutButtonBar.observe(document.forms.sign);
});

const maybeFadeOutButtonBar = new window.IntersectionObserver(
	([form]) => buttonBar && buttonBar.classList.toggle('petition-fade-out', form.isIntersecting),
	{ threshold: 0.1 } // 10% in viewport
);

/**
 * Replaces the sign-state message elements with an already-signed notice and reveals them.
 * @param {string} [message='You have already signed this petition. Thank you for your support!']
 * @returns {boolean} True if message elements were found and updated, false otherwise
 */
const revealAlreadySignedMessage = (message = 'You have already signed this petition. Thank you for your support!') => {
	const msgEls = Array.from(document.querySelectorAll('[data-cusa-sign-state-msg]'));
	if (!msgEls.length) return false;
	msgEls.forEach((el) => {
		el.innerHTML = `<span class='already-signed-text'>${message}</span>`;
		el.hidden = false;
		el.setAttribute('aria-hidden', 'false');
		el.querySelector('.loading__spinner')?.classList.add('hidden');
	});
	return true;
};

/**
 * @param {HTMLElement} el
 * @returns {void}
 */
const slideOpen = (el) => (el.style.height = `${el.scrollHeight}px`);

/**
 * @param {HTMLFormElement} form
 * @returns {void}
 */
const signingStateUpdate = (form) => {
	const submitButton = form.querySelector('button[type="submit"]');
	if (!submitButton) return;

	// Prevent duplicate submissions while a request is in-flight.
	if (form.dataset.isSigning === 'true') return;
	form.dataset.isSigning = 'true';

	// Preserve the original label so we can restore it on error.
	if (!submitButton.dataset.originalText) {
		submitButton.dataset.originalText = submitButton.textContent || '';
	}

	submitButton.setAttribute('disabled', '');
	submitButton.textContent = 'Signing...';
};

/**
 * Re-enables the submit button and restores its original label after an error.
 * @param {HTMLFormElement} form
 * @returns {void}
 */
const restoreSigningState = (form) => {
	const submitButton = form.querySelector('button[type="submit"]');
	if (!submitButton) return;
	form.dataset.isSigning = 'false';
	// Do not clear `hasSigned` here; errors should allow retry, but success locks the form.
	submitButton.removeAttribute('disabled');
	submitButton.textContent = submitButton.dataset.originalText || submitButton.textContent || 'Sign';
};

/**
 * Fires the signature and support-step events for a single funnel entry.
 *
 * `signedStateUpdate` is the choke point for all three ways a user reaches the support
 * step, but it cannot infer which one occurred: a duplicate signature and a logged-in
 * member routed straight to support both arrive with no formData. The caller therefore
 * states the reason explicitly, and callers that can't (the forced re-check inside
 * applySignatureState) pass nothing and track nothing.
 *
 * A 409 runs signedStateUpdate twice — once via applySignatureState(true), once directly
 * from errorStateUpdate — so the flag guards against double-counting the support step.
 *
 * @param {HTMLFormElement} form
 * @param {'new'|'duplicate'|null} signatureResult - Null to skip the signature event.
 * @param {string=} entryReason - Omit to skip tracking entirely.
 * @returns {void}
 */
const trackFunnelEntry = (form, signatureResult, entryReason) => {
	if (!entryReason || !window.CusaPetitionFunnelAnalytics) return;
	if (form?.dataset?.funnelEntryTracked === 'true') return;
	if (form?.dataset) form.dataset.funnelEntryTracked = 'true';

	if (signatureResult) window.CusaPetitionFunnelAnalytics.trackSignature(signatureResult);
	window.CusaPetitionFunnelAnalytics.trackStepView('support', entryReason);
};

/**
 * @param {HTMLFormElement} form
 * @param {FormData} formData
 * @param {string=} entryReason - One of after_new_signature, after_duplicate_signature,
 *   direct_logged_in. Omit to suppress funnel analytics for this call.
 * @returns {void}
 */
const signedStateUpdate = (form, formData, entryReason) => {
	// Lock the form after a successful submission so it can't be re-submitted.
	form.dataset.isSigning = 'false';
	form.dataset.hasSigned = 'true';
	tearDownForm(form);
	window.scrollTo({ top: 0 });
	successStateUI.forEach((el) => slideOpen(el));
	const funnelRoot = document.getElementById('petition-post-signature-funnel');
	const funnelReveal = funnelRoot?.closest('.reveal');
	if (funnelReveal) {
		funnelReveal.addEventListener(
			'transitionend',
			() => {
				const stickyHeader = document.querySelector('sticky-header');
				const headerBottom = stickyHeader ? stickyHeader.getBoundingClientRect().bottom : 0;
				const funnelTop = funnelRoot.getBoundingClientRect().top + window.scrollY;
				window.scrollTo({ top: funnelTop - headerBottom - 12, behavior: 'smooth' });
			},
			{ once: true }
		);
	}
	if (formData) {
		addSignatureToList(formData);
		incrementTotalSignatures();
	}

	trackFunnelEntry(form, formData ? 'new' : entryReason === 'after_duplicate_signature' ? 'duplicate' : null, entryReason);
};

/**
 * Restores the form to an interactive state and surfaces an error toast.
 * Also logs the error via window.logFrontendError for backend visibility.
 * 409s are treated as success (already signed) and bypass the error path entirely.
 *
 * @param {HTMLFormElement} form
 * @param {number=} errorStatus - HTTP status code returned by the endpoint.
 * @param {string=} errorString - Human-readable error message to display and log.
 * @param {string=} endpoint - Middleware endpoint that failed. Defaults to petition signatures.
 * @param {string=} errorType - Error type label sent to the logger. Defaults to 'PetitionSignatureError'.
 * @returns {void}
 */
const errorStateUpdate = (form, errorStatus, errorString, endpoint = CUSA_ENDPOINTS.petitionSignatures, errorType = 'PetitionSignatureError') => {
	if (errorStatus && errorStatus === 409) {
		if (typeof window.applySignatureState === 'function') {
			window.applySignatureState(true);
		} else {
			revealAlreadySignedMessage();
		}
		signedStateUpdate(form, undefined, 'after_duplicate_signature');
		return;
	}
	restoreSigningState(form);
	window.logFrontendError?.({
		error_message: errorString,
		error_type: errorType,
		context: { status: errorStatus, endpoint }
	});
	CusaToastNotification.create({
		title: "We're sorry, we've encountered an error. Please try again.",
		message: `Error code ${errorStatus}: "${errorString}".\n\nUrl: ${window.location.href}\n\nIf you continue to enouncter this error, please contact our Customer Care Team at customerservice@greatergood.com with the information above.`,
		type: 'error',
		duration: 'infinite'
	});
};

/**
 * @param {HTMLFormElement} form
 * @returns {void}
 */
const tearDownForm = (form) => {
	form.reset();
	// hiding form's container
	form.parentElement.classList.add('hidden');
	// tearing down mobile CTA
	maybeFadeOutButtonBar.unobserve(document.forms.sign);
	buttonBar.classList.add('hidden');
};

/**
 * Recent signatures have full name fields, but we're constructing one here
 * @param {FormData} formData
 * @returns {void}
 */
const addSignatureToList = (formData) => {
	const listItem = document.getElementById('petition-signatory');
	let fullName = 'Anonymous';
	if (!formData.has('is_anonymous')) {
		const firstName = formData.get('first_name');
		const lastName = formData.get('last_name');
		if (firstName && lastName) fullName = `${firstName} ${lastName}`;
	}
	if (listItem) listItem.textContent = fullName;
};

/**
 * @returns {void}
 */
const incrementTotalSignatures = () => {
	const progressBar = document.getElementById('petition-progress');
	let totalSignatures = progressBar.value;
	const signatureGoal = progressBar.max;
	const totalSignaturesStrings = document.querySelectorAll('.petition-total-signatures-string');
	if (
		progressBar &&
		'number' === typeof totalSignatures &&
		'number' === typeof signatureGoal &&
		totalSignaturesStrings
	) {
		totalSignatures++; // user's signature
		progressBar.value = totalSignatures;
		totalSignaturesStrings.forEach((str) => (str.textContent = totalSignatures.toLocaleString()));
		if (totalSignatures >= signatureGoal) {
			// if we met goal or somehow exceeded it, revise UI to reflect that
			progressBar.textContent = '100% Complete';
			const container = document.getElementById('petition-goal');
			if (container) {
				const toHide = container.querySelector('[data-complete="false"]');
				const toShow = container.querySelector('[data-complete="true"]');
				toHide && toHide.classList.add('hidden');
				toShow && toShow.classList.remove('hidden');
			}
			const contributeTitle = document.getElementById('petition-contribute-title');
			if (contributeTitle) contributeTitle.textContent = 'Help Us Reach New Supporters';
		} else {
			const percentage = Math.round((totalSignatures / signatureGoal) * 100);
			progressBar.textContent = `${percentage.toString()}% Complete`;
		}
	}
};

/**
 * @param {SubmitEvent} event
 * @returns {void}
 */
async function signPetition(event) {
	event.preventDefault();
	/** @type {HTMLFormElement} */
	const form = event.target;
	// If the form has already been successfully submitted, ignore further attempts.
	if (form.dataset.hasSigned === 'true') return;
	const formData = new FormData(form);
	const signature = { petition_signature: Object.fromEntries(formData) };

	signingStateUpdate(form);

	console.group('Signature:');
	console.log('data to be sent:');
	console.log(signature);

	try {
		const res = await loggedFetch(CUSA_ENDPOINTS.petitionSignatures, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(signature)
		});
		// Only use this to test 409 errors locally since they always return 422 for some weird reason using the cli local server
		// const res = await mockFetch({
		// 	response: { status: 409 },
		// 	delay: 500,
		// 	shouldReject: true,
		// 	format: 'json',
		// 	useCusaFetchFormat: true
		// });
		let data = null;
		let textFallback = '';

		// Read the body ONCE. `res.json()` consumes the stream even when it throws.
		try {
			textFallback = await res.text();
		} catch (readError) {
			textFallback = '';
		}

		// If it looks like JSON (by header or by content), attempt to parse.
		const contentType = res.headers.get('content-type') || '';
		const trimmed = (textFallback || '').trim();
		const maybeJson = contentType.includes('application/json') || trimmed.startsWith('{') || trimmed.startsWith('[');
		if (maybeJson && trimmed) {
			try {
				data = JSON.parse(trimmed);
			} catch (parseError) {
				data = null;
			}
		}

		if (res.ok) {
			console.log('signature accepted:');
			console.log(data);
			signedStateUpdate(form, formData, 'after_new_signature');
			setGGGWinVarAndPub(data);
		} else {
			let msg = data?.error || data?.message;

			if (!msg) {
				const t = (textFallback || '').trim();
				// If this is an HTML error page, avoid dumping it; give a compact hint.
				if (t && /<\/?(html|!doctype|body|head)\b/i.test(t)) {
					msg = 'Server returned an unexpected error page.';
				} else {
					msg = t || res.statusText || 'Unknown error';
				}
			}

			errorStateUpdate(form, res.status, String(msg));
		}
	} catch (error) {
		let errorStatus = typeof error?.status === 'number' ? error.status : 0;
		let errorMessage = error?.message ?? 'Unknown error';
		if (typeof error?.json === 'function') {
			try {
				const parsed = await error.json();
				errorMessage = parsed?.error ?? parsed?.message ?? errorMessage;
			} catch (parseError) {
				console.warn('Could not parse error body', parseError);
			}
		}
		errorStateUpdate(form, errorStatus, errorMessage);
	}

	console.groupEnd();
}
